// Turning the names printed on a confirmation into this site's entities.
//
// A confirmation says "Lebanon, KY", "NGMO Corn", "Homestead Family Farms General
// Partnership". None of those are ids, and none match a site record exactly, so each is
// resolved with the same tolerant matcher the importers use. Anything that does not
// resolve is returned as an unresolved entry rather than guessed at - the review screen
// shows them and the user picks.
//
// What the user picks comes back in as overrides. A picked entity replaces the name
// match outright, so a blank or misspelt name on the confirmation stops being a blocker
// the moment the right record is chosen.
import {
    AllocationModel,
    CommodityModel,
    Currency,
    GetCustomerDataResponse,
    LocationModel,
    PositionModel,
    Unit,
} from '../../../../Generated/Commodity-Management-Api';
import { findBestEntityMatch } from '../../../../Helpers';
import { EfpContractFields, getMissingEfpFields } from './EfpContractParser';
import { EfpBasisSource, EfpPlanInput } from './EfpPositionPlan';

/**
 * Entities the user chose on the review screen, by id. `undefined` means "resolve from
 * the name on the contract"; `null` means the user cleared the pick and nothing should
 * be selected.
 */
export interface EfpEntityOverrides {
    commodityId?: number | null;
    counterpartyId?: number | null;
    locationId?: number | null;
}

export interface EfpResolution {
    commodity?: CommodityModel;
    counterparty?: { counterpartyId: number; counterpartyName: string };
    location?: LocationModel;
    parentLocation?: LocationModel;
    allocationGroup?: AllocationModel;
    /** Field labels the confirmation never supplied. */
    missingFields: string[];
    /** Things that were supplied but could not be matched to site data. */
    unresolved: string[];
}

export function resolveEfpContract(
    fields: EfpContractFields,
    customerData: GetCustomerDataResponse,
    allocations: readonly AllocationModel[],
    overrides: EfpEntityOverrides = {}
): EfpResolution {
    const unresolved: string[] = [];

    const commodities = customerData.commodities ?? [];
    const counterparties = customerData.counterparties ?? [];
    const locations = customerData.locations ?? [];

    let commodity: CommodityModel | undefined;
    if (overrides.commodityId !== undefined) {
        commodity = commodities.find(candidate => candidate.commodityId === overrides.commodityId);
        if (!commodity) unresolved.push('No commodity has been chosen');
    } else {
        commodity = fields.commodityName
            ? findBestEntityMatch(fields.commodityName, commodities, 'commodityName').item as CommodityModel | undefined
            : undefined;
        if (fields.commodityName && !commodity) {
            unresolved.push(`Commodity "${fields.commodityName}" does not match a commodity on this site`);
        }
    }

    let counterparty: { counterpartyId: number; counterpartyName: string } | undefined;
    if (overrides.counterpartyId !== undefined) {
        counterparty = counterparties.find(candidate => candidate.counterpartyId === overrides.counterpartyId);
        if (!counterparty) unresolved.push('No seller has been chosen');
    } else {
        counterparty = fields.sellerName
            ? findBestEntityMatch(fields.sellerName, counterparties, 'counterpartyName').item as
                { counterpartyId: number; counterpartyName: string } | undefined
            : undefined;
        if (fields.sellerName && !counterparty) {
            unresolved.push(`Seller "${fields.sellerName}" does not match a counterparty on this site`);
        }
    }

    let location: LocationModel | undefined;
    if (overrides.locationId !== undefined) {
        location = locations.find(candidate => candidate.locationId === overrides.locationId);
        if (!location) unresolved.push('No plant has been chosen');
    } else {
        // "Lebanon, KY" - the state suffix is not part of the location name, so a failed
        // match is retried on the part before the comma.
        location = fields.plantName
            ? findBestEntityMatch(fields.plantName, locations, 'locationName').item as LocationModel | undefined
            : undefined;

        if (!location && fields.plantName?.includes(',')) {
            const beforeComma = fields.plantName.split(',')[0].trim();
            location = findBestEntityMatch(beforeComma, locations, 'locationName').item as LocationModel | undefined;
        }

        if (fields.plantName && !location) {
            unresolved.push(`Plant "${fields.plantName}" does not match a location on this site`);
        }
    }

    const parentLocation = location?.parentLocationId
        ? locations.find(candidate => candidate.locationId === location!.parentLocationId)
        : undefined;

    if (location && !parentLocation) {
        unresolved.push(`${location.locationName} has no parent location, so there is no hedge to transfer from`);
    }

    // The group is keyed on exactly the three things the spec names.
    const allocationGroup = location && counterparty && commodity
        ? allocations.find(group =>
            group.location?.locationId === location!.locationId
            && group.counterparty?.counterpartyId === counterparty!.counterpartyId
            && group.commodity?.commodityId === commodity!.commodityId)
        : undefined;

    if (location && counterparty && commodity && !allocationGroup) {
        unresolved.push(
            `No allocation group for ${location.locationName} / ${counterparty.counterpartyName} / ${commodity.commodityName}`
        );
    }

    // A name the confirmation left blank is not missing once the user has picked the
    // record it should have named.
    const suppliedByPick: Record<string, boolean> = {
        Commodity: !!commodity,
        Seller: !!counterparty,
        Plant: !!location,
    };
    const missingFields = getMissingEfpFields(fields).filter(label => !suppliedByPick[label]);

    return {
        commodity,
        counterparty,
        location,
        parentLocation,
        allocationGroup,
        missingFields,
        unresolved,
    };
}

/** True when there is enough to build a plan from. */
export function canPlanEfpContract(fields: EfpContractFields, resolution: EfpResolution): boolean {
    return resolution.missingFields.length === 0
        && !!resolution.commodity
        && !!resolution.counterparty
        && !!resolution.location
        && fields.futuresMonth !== undefined
        && fields.deliveryMonth !== undefined
        && fields.volume !== undefined
        && fields.unit !== undefined
        && fields.boardPrice !== undefined
        && fields.basisPrice !== undefined;
}

/**
 * Assemble the plan input. Only call when canPlanEfpContract passes - the non-null
 * assertions are what that check guarantees.
 */
export function toEfpPlanInput(
    fields: EfpContractFields,
    resolution: EfpResolution,
    existingPositions: readonly PositionModel[],
    overrides: { tradeDate?: string; currency?: Currency; basisSource?: EfpBasisSource } = {}
): EfpPlanInput {
    return {
        commodity: resolution.commodity!,
        counterparty: resolution.counterparty as EfpPlanInput['counterparty'],
        location: resolution.location!,
        parentLocation: resolution.parentLocation,
        allocationGroup: resolution.allocationGroup,
        volume: fields.volume!,
        unit: fields.unit as Unit,
        currency: overrides.currency ?? fields.currency ?? Currency.Usd,
        boardPrice: fields.boardPrice!,
        boardPriceUnit: fields.boardPriceUnit,
        basisPrice: fields.basisPrice!,
        basisPriceUnit: fields.basisPriceUnit,
        futuresMonth: fields.futuresMonth!,
        deliveryMonth: fields.deliveryMonth!,
        tradeDate: overrides.tradeDate ?? fields.contractDate ?? new Date().toISOString().slice(0, 10),
        dealTicketNumber: fields.contractNumber,
        existingPositions,
        basisSource: overrides.basisSource,
    };
}
